import { z } from 'zod';
import { normaliseSectionName } from '../utils/ingestName.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [amended SEED] THE ESTABLISHMENT FILE — the contract for
 *  `api/src/seed/data/establishment.json`.
 *
 *  That file is the RECORD OF WHAT THE CLIENT SENT: one commissionerate, ten divisions,
 *  seventy-eight police stations. It is validated here at LOAD TIME, not trusted — a seed
 *  file is input like any other, and this one builds the tree FR-3.6's whole isolation
 *  model is computed from.
 *
 *  ⚠️ SEVERAL STATION NAMES LOOKED LIKE TYPOS AND WERE NOT CORRECTED — "Kolkata Lether
 *  Complex", "Bijougunjge bajar", "Bhangar /Bodra", "Amherst St.". They were reproduced as
 *  supplied and raised as queries for the client.
 *
 *  ⚠️ [amended INGEST.3] THOSE QUERIES ARE ANSWERED, AND THE RULE IS UNCHANGED. Eleven names
 *  now carry the client's corrections — "Kol. Leather Complex", "Bijoyganj Bazar",
 *  "Bhangar", "Amherst Street", "Entally", "West Port"… — applied to the live establishment
 *  first and transcribed back into the file so the two agree. That is exactly the sentence
 *  below, honoured: a correction is an edit to the JSON, in a commit, with the client's
 *  confirmation attached — never a transform in a script, where it becomes a rule nobody
 *  can see and nobody can diff. A silently corrected police station name is a name that
 *  will not match the client's own records, and nobody will know why.
 *
 *  ⚠️ AND THE OLD SPELLINGS SURVIVE AS ALIASES, so correcting a name cost no match at all.
 *  That is the whole reason the mapping is DATA rather than a transform.
 *
 *  ─── ⚠️ THIS SCHEMA REJECTS UNKNOWN KEYS. CLAUDE.md §6 STRIPS THEM. ───────────────
 *
 *  §6's strip-by-default rule governs UNTRUSTED HTTP INPUT, where stripping IS the
 *  mass-assignment defence: the sender must not choose the field set, and they get a
 *  status code back. A committed seed file inverts all three.
 *
 *    • There is no privilege to escalate. This file is already the most privileged input
 *      in the system.
 *    • The failure mode is not "a field gets set", it is "a field is SILENTLY DROPPED".
 *      `psCodes`, `sectionCOde`, `policestations` each strip to a clean parse, and the
 *      seeder then reports "78 stations — no changes" while the correction the client
 *      sent never lands. There is no 422 for anyone to read.
 *    • The file is the record. A key this schema does not understand is either a
 *      transcription error or a column the client has added. Both are things a human
 *      must see; neither is something to discard.
 *
 *  Strip protects a boundary where the sender must not pick the fields.
 *  Strict protects a boundary where the sender must be TOLD their field was not read.
 *  Different boundary, opposite rule — and that is why this is marked as a deviation
 *  rather than left to look like an oversight.
 *
 *  ⚠️ NO `.trim()` AND NO CASE TRANSFORM ANYWHERE IN THIS FILE. A transform here lets the
 *  record and the database disagree about what the client sent. Surrounding whitespace is
 *  REJECTED rather than repaired, for the same reason an unknown key is; and a lower-case
 *  code is rejected rather than upper-cased — see `sectionCodeSchema`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * Bumped only in a commit that also changes the ingest, and matched with `z.literal`, so
 * a file written for a newer ingest refuses rather than being half-understood.
 *
 * ⚠️ [amended INGEST] 1 → 2. Two REQUIRED keys arrived — `aliases` on a station and
 * `womenPoliceStations` on a division — and this is exactly the commit the sentence
 * above describes. A version-1 file now refuses with one sentence naming the version,
 * rather than failing 88 times with "Required" against paths its author never heard of.
 */
export const ESTABLISHMENT_FILE_VERSION = 2;

/**
 * ⚠️ UPPER CASE IS PART OF THE PATTERN, AND THAT IS NOT A CONTRADICTION OF
 * "SECTION CODES ARE CASE-SENSITIVE".
 *
 * Case-sensitive means nothing here NORMALISES the value — there is no `.toUpperCase()`
 * on this path, unlike `orgUnitBaseSchema.shortCode`. The regex is a separate matter: a
 * station's `org_units.short_code` is DERIVED from its section code (see
 * `establishment.plan.ts` for why that derivation is ours to make rather than a
 * correction of the client's data), and `short_code` carries `uppercase: true` on the
 * model. A lower-case section code would therefore be stored as two different strings in
 * two columns, and the seeder's idempotency lookup would miss its own row on the second
 * run. Refusing is loud and fixable by editing the file; upper-casing would be a silent
 * rewrite of the record.
 *
 * All 78 codes the client supplied match this today.
 */
export const ESTABLISHMENT_SECTION_CODE_RE = /^[A-Z0-9]{1,4}$/;
/** The ten division codes: SED SSD SWD ED BD ND ESD CD SD PD. */
export const ESTABLISHMENT_DIVISION_CODE_RE = /^[A-Z]{2,3}$/;
export const ESTABLISHMENT_ROOT_CODE_RE = /^[A-Z]{2,4}$/;
/** Reserved. Nothing in this import carries one; the shape is declared so the day one arrives is a data edit. */
export const ESTABLISHMENT_PS_CODE_RE = /^[A-Z0-9-]{1,24}$/;

/**
 * ─── [amended INGEST] AN ALIAS IS A FOREIGN STRING, AND IT LOOKS LIKE ONE ────────
 *
 * UPPER CASE, because every station `name` in this file is title case and the two sit in
 * the same object. A reviewer must never have to work out which of `"Bhangar /Bodra"`
 * and `"BHANGAR"` is the establishment's own name and which is somebody else's rendering
 * of it — the casing answers that at a glance. This is a format rule on a value WE
 * derive, not a transform of the client's record, so it does not contradict this file's
 * no-case-transform doctrine: an alias is refused, never upper-cased for you.
 *
 * `.`, `/`, `(`, `)`, `&` and `'` are admitted because the source uses them —
 * `KOL. LEATHER COMPLEX`, `BHANGAR /BODRA`. `normaliseSectionName` does NOT reduce them,
 * so an alias carrying one must carry it exactly as the source does.
 */
export const ESTABLISHMENT_ALIAS_RE = /^[A-Z0-9][A-Z0-9 ./()&'-]*$/;

/**
 * ⚠️ AT MOST TWELVE, AND THE BOUND IS A CORRUPTION TRIPWIRE RATHER THAN A BUDGET.
 * The busiest station in this file carries one. A row that has grown twelve spellings is
 * a row somebody has pasted a column into.
 */
export const ESTABLISHMENT_MAX_ALIASES = 12;

const isExact = (value: string): boolean => value === value.trim();
const EXACT_MESSAGE = 'Remove the leading or trailing space — this file is stored exactly as it is written.';

const establishmentName = z.string().min(1, 'A name is required').max(160).refine(isExact, EXACT_MESSAGE);

/**
 * ⚠️ UNIT-ONLY, ENFORCED BY THE TYPE RATHER THAN BY A CONVENTION.
 *
 * `strictObject` alone would already refuse these keys on a division, but only as
 * "Unrecognized key" — true, and it says nothing about why. Declaring the path as
 * `z.undefined()` with a message puts the REASON at the exact path. An absent key still
 * parses clean and is not added to the output object.
 */
const unitOnly = (field: 'sectionCode' | 'psCode') =>
  z
    .undefined({
      error:
        `\`${field}\` belongs to a police station, not to a division or the commissionerate. ` +
        'A section is an operational area; the nodes above it do not have one.',
    })
    .optional();

export const establishmentPoliceStationSchema = z.strictObject({
  name: establishmentName,
  /**
   * The station's identity — `org_units.section_code`, and the seeder's idempotency key.
   * Unique across the whole file, which the `superRefine` below checks by name.
   */
  sectionCode: z
    .string()
    .regex(
      ESTABLISHMENT_SECTION_CODE_RE,
      'A section code is 1–4 characters, A–Z and 0–9, upper case. Nothing here upper-cases it for you.',
    ),
  /**
   * RESERVED, and `null` on all 78 rows of this import. The key is REQUIRED so that it is
   * visible on every one of them — "we have not been given the PS codes" then reads as a
   * fact the file states 78 times rather than one inferred from an absence.
   *
   * ⚠️ `null` HERE MEANS ABSENT IN MONGO. `org_units.ps_code` carries a partial unique
   * index on `{ $type: 'string' }` and the model's `omitBlank` setter turns null into an
   * omitted path, so the ingest never writes the column at all. Making this `.optional()`
   * instead would collapse "reserved" and "absent" into one wire value — which is exactly
   * the distinction the index depends on.
   */
  psCode: z.string().regex(ESTABLISHMENT_PS_CODE_RE, 'A PS code is 1–24 characters, A–Z, 0–9 and "-".').nullable(),
  /**
   * ─── [amended INGEST] EVERY OTHER STRING THIS STATION ANSWERS TO ───────────────
   *
   * The officer source spells a station differently from the establishment — `BHANGAR PS`
   * for "Bhangar /Bodra", `GIRISH` for "Girish Park", `ENTALLY` for "Entaly". Each such
   * spelling is a row HERE, so the mapping is a line in a file a reviewer can diff and
   * the client can be asked about — never a branch in the resolver, and never a
   * correction to the `name` above, which is the record of what the client sent.
   *
   * ⚠️ THE ALIAS TARGET IS THE `sectionCode`, NOT THE NAME. Codes are unique across the
   * whole establishment and immune to exactly the spelling problem the aliases exist for.
   * `officerIngest` reports a resolution as "→ BDR", and BDR cannot be misread.
   *
   * ⚠️ REQUIRED, AND `[]` IS A POSITIVE STATEMENT. Same argument as `psCode` above: an
   * empty array on 53 of the 87 stations says "this one was checked and the source spells
   * it the way we do", which an absent key cannot say. It is also what makes the stations
   * that DO need one findable by eye.
   *
   * ⚠️ REDUNDANT ALIASES ARE ALLOWED ON PURPOSE. `BELIAGHATA` on the station named
   * "Beliaghata" already matches by name, and it is listed anyway — the brief named it,
   * so it is a DECLARED fact rather than an accident of normalisation, and it keeps
   * matching the day somebody corrects the name. What is refused is an AMBIGUOUS alias:
   * see the `superRefine` below.
   */
  aliases: z
    .array(
      z
        .string()
        .min(1, 'An empty alias matches nothing. Remove it.')
        .max(80)
        .refine(isExact, EXACT_MESSAGE)
        .regex(
          ESTABLISHMENT_ALIAS_RE,
          'An alias is UPPER CASE — it is the source spreadsheet’s rendering, not our name for the station. ' +
            "A–Z, 0–9, space and . / ( ) & ' - are allowed. Nothing here upper-cases it for you.",
        )
        .refine(
          (value) => !/ PS$/.test(value),
          'Write the alias without the trailing "PS". The resolver strips one before matching, so ' +
            '"BHANGAR PS" and "BHANGAR" are the same key — and listing both makes one of them unreachable.',
        ),
    )
    .max(
      ESTABLISHMENT_MAX_ALIASES,
      `At most ${String(ESTABLISHMENT_MAX_ALIASES)} aliases. More than that is a pasted column, not a spelling.`,
    ),
});

export const establishmentDivisionSchema = z.strictObject({
  name: establishmentName,
  shortCode: z
    .string()
    .regex(ESTABLISHMENT_DIVISION_CODE_RE, 'A division short code is two or three upper-case letters, e.g. SED or ED.'),
  sectionCode: unitOnly('sectionCode'),
  psCode: unitOnly('psCode'),
  policeStations: z
    .array(establishmentPoliceStationSchema)
    .min(1, 'A division with no police stations is almost certainly an editing accident. Delete it, or fill it in.'),
  /**
   * ─── [amended INGEST] THE WOMEN'S POLICE STATIONS — A SECOND LIST, NOT A FLAG ───
   *
   * Nine of them, one per division except Bhangar. They are real stations with real
   * officers and none was in the file the client supplied on 2026-08-11.
   *
   * ⚠️ A SEPARATE KEY RATHER THAN AN `isWomen` FLAG ON `policeStations`, AND THE REASON
   * IS PROVENANCE. `policeStations` is a TRANSCRIPTION of the client's own sheet, and
   * `establishmentPoliceStationSchema` is `strictObject` — adding a required flag would
   * have meant editing all seventy-eight of those rows, so the record of what was sent
   * would no longer be diffable against the sheet it came from. A second array leaves
   * every original row byte-identical and puts the new population where its different
   * origin is visible.
   *
   * ⚠️ REQUIRED AND POSSIBLY EMPTY, unlike `policeStations`' `.min(1)`. Bhangar Division
   * has no women's station, and `"womenPoliceStations": []` says that. An absent key
   * would say the same thing by omission, which is the distinction `psCode` above exists
   * to refuse: a fact the file states is a fact somebody checked.
   *
   * ⚠️ ONE NAMESPACE STILL. Their section codes are claimed in exactly the same
   * `superRefine` pass as the ordinary ones — a women's station code colliding with a
   * division code is the same duplicate-key error part-way through the same transaction.
   * They differ from an ordinary station in exactly one place downstream: the planner
   * gives them `subType: 'WOMEN_PS'`.
   */
  womenPoliceStations: z.array(establishmentPoliceStationSchema),
});

export const establishmentCommissionerateSchema = z.strictObject({
  name: establishmentName,
  shortCode: z
    .string()
    .regex(ESTABLISHMENT_ROOT_CODE_RE, 'A commissionerate short code is two to four upper-case letters.'),
  sectionCode: unitOnly('sectionCode'),
  psCode: unitOnly('psCode'),
});

export const establishmentFileSchema = z
  .strictObject({
    version: z.literal(ESTABLISHMENT_FILE_VERSION, {
      error: `This ingest understands establishment file version ${String(ESTABLISHMENT_FILE_VERSION)} only.`,
    }),
    /** Provenance. Copied verbatim into every audit row the import writes. */
    source: z.string().min(1).max(240).refine(isExact, EXACT_MESSAGE),
    commissionerate: establishmentCommissionerateSchema,
    divisions: z.array(establishmentDivisionSchema).min(1),
  })
  /**
   * ⚠️ ONE NAMESPACE, AND IT IS WIDER THAN "SECTION CODES ARE UNIQUE".
   *
   * The commissionerate code, the ten division codes AND the 78 section codes all land in
   * `org_units.short_code`, which is PLAIN unique — a station's `short_code` IS its
   * section code. So a collision between any two of the three is a duplicate-key error
   * part-way through a transaction, naming a key and no row. It is caught HERE, where
   * both sides can be named.
   *
   * `superRefine` with one `addIssue` per offending row, rather than several `.refine`s
   * or a throw on the first: a hand-edited file should tell its editor everything wrong
   * with it in one pass. Each issue carries its own `path`, so the message names the row.
   *
   * ⚠️ This checks the FILE against ITSELF. A code already taken in the DATABASE by
   * something this import does not own — a `seed:demo` unit, a hand-created node — is a
   * different check, and it lives in `establishment.plan.ts` where the existing rows are
   * in hand. Neither subsumes the other.
   */
  .superRefine((file, ctx) => {
    /**
     * ⚠️ [amended INGEST] EVERY STATION CHECK BELOW RUNS OVER **BOTH** LISTS.
     *
     * `womenPoliceStations` is a second array of exactly the same shape, and the whole
     * cost of that decision is this one flattening — everything downstream reads
     * `stations` and cannot tell which array a row came from except through `subType`.
     * Writing the loops twice was the alternative, and a check that exists once for
     * ordinary stations and not for women's ones is the failure this pass is about.
     */
    const stations = file.divisions.flatMap((division, d) => [
      ...division.policeStations.map((station, s) => ({
        division,
        station,
        path: ['divisions', d, 'policeStations', s] as (string | number)[],
      })),
      ...division.womenPoliceStations.map((station, s) => ({
        division,
        station,
        path: ['divisions', d, 'womenPoliceStations', s] as (string | number)[],
      })),
    ]);

    /**
     * ⚠️ A NON-NULL `psCode` IS REFUSED, BECAUSE THE INGEST DOES NOT WRITE IT YET.
     *
     * The shape allows a string so that the day the client supplies the codes is a data
     * edit rather than a schema rewrite — but nothing downstream reads one today:
     * `establishment.plan.ts`'s `diff` does not compare it, and `orgUnitDocFields` has no
     * `psCode` parameter. A supplied code would therefore validate, plan as UNCHANGED and
     * land nowhere, and the operator would be told "78 already current" — which is exactly
     * the SILENT DROP this file's whole strict-parsing argument exists to prevent. Better
     * to refuse with the sentence that names the two places to change.
     */
    for (const { division, station, path } of stations) {
      if (station.psCode === null) continue;
      ctx.addIssue({
        code: 'custom',
        path: [...path, 'psCode'],
        message:
          `A PS code was supplied for "${division.name} / ${station.name}", and the ingest does not write one ` +
          'yet — it would be accepted here and silently dropped. Wire it through `diff` in ' +
          '`api/src/seed/establishment.plan.ts` and `orgUnitDocFields` in ' +
          '`api/src/services/orgUnit.service.ts` first, then remove this check.',
      });
    }

    /**
     * ─── [amended INGEST] AN ALIAS MAY NOT BE AMBIGUOUS WITHIN ITS DIVISION ───────
     *
     * The resolver looks a section string up inside ONE division, so that is the scope
     * uniqueness has to hold over — two divisions may both answer to `HARE` without any
     * confusion, and forbidding that would be a rule about nothing.
     *
     * ⚠️ THE KEYS COMPARED ARE THE **NORMALISED** ONES, THROUGH THE RESOLVER'S OWN
     * FUNCTION. `GIRISH` and `girish-ps` are one key, and a check that compared the raw
     * strings would call them distinct, pass, and leave the resolver with two candidates
     * and no rule for choosing — which it would settle by declaration order, silently.
     * Importing `normaliseSectionName` is what makes this check mean the same thing the
     * resolver means.
     *
     * ⚠️ A STATION'S OWN NAME IS A KEY TOO, and it is claimed FIRST for every station in
     * the division before any alias is. So an alias that collides with another station's
     * NAME is reported against the ALIAS — the thing somebody just added — rather than
     * against the station that has been sitting there since the client sent the file.
     * A key that a station claims twice (a redundant alias equal to its own name) is
     * explicitly fine; it is the same station, so there is nothing to disambiguate.
     */
    file.divisions.forEach((division, d) => {
      const divisionStations = [
        ...division.policeStations.map((station, s) => ({ station, path: ['divisions', d, 'policeStations', s] })),
        ...division.womenPoliceStations.map((station, s) => ({
          station,
          path: ['divisions', d, 'womenPoliceStations', s],
        })),
      ];

      /** normalised key -> the section code that already answers to it. */
      const answersTo = new Map<string, { code: string; name: string }>();
      for (const { station } of divisionStations) {
        answersTo.set(normaliseSectionName(station.name), { code: station.sectionCode, name: station.name });
      }

      for (const { station, path } of divisionStations) {
        station.aliases.forEach((alias, a) => {
          const key = normaliseSectionName(alias);
          const holder = answersTo.get(key);
          if (holder === undefined || holder.code === station.sectionCode) {
            answersTo.set(key, { code: station.sectionCode, name: station.name });
            return;
          }
          ctx.addIssue({
            code: 'custom',
            path: [...path, 'aliases', a],
            message:
              `In ${division.name}, the alias "${alias}" reduces to "${key}", which "${holder.name}" ` +
              `(${holder.code}) already answers to. An officer whose section reads "${alias}" could belong to ` +
              `either that station or "${station.name}" (${station.sectionCode}), and the resolver has no way ` +
              'to choose. Remove one, or make the two spellings distinct.',
          });
        });
      }
    });

    /** code -> a human description of whoever claimed it first. */
    const claimed = new Map<string, string>([
      [file.commissionerate.shortCode, `the commissionerate "${file.commissionerate.name}"`],
    ]);

    /*
     * ⚠️ TWO PASSES, AND THE ORDER DECIDES WHERE THE COMPLAINT LANDS. The structural
     * codes — the commissionerate and the ten divisions — are claimed FIRST, so a station
     * whose section code collides with a division is reported against the STATION rather
     * than against the division that happens to be declared after it. The spine of the
     * tree is not the thing to go and edit when a section code is wrong.
     */
    file.divisions.forEach((division, d) => {
      const owner = claimed.get(division.shortCode);
      if (owner === undefined) {
        claimed.set(division.shortCode, `division "${division.name}"`);
      } else {
        ctx.addIssue({
          code: 'custom',
          path: ['divisions', d, 'shortCode'],
          message:
            `Short code ${division.shortCode} is already used by ${owner}. ` +
            `Division "${division.name}" needs a different one.`,
        });
      }
    });

    /*
     * ⚠️ [amended INGEST] ORDINARY AND WOMEN'S STATIONS SHARE ONE CODE NAMESPACE, so this
     * runs over the flattened list. `U1W` colliding with a division code is the same
     * E11000, in the same transaction, as `U1` would be.
     */
    for (const { division, station, path } of stations) {
      const holder = claimed.get(station.sectionCode);
      if (holder === undefined) {
        claimed.set(station.sectionCode, `"${division.name} / ${station.name}"`);
      } else {
        ctx.addIssue({
          code: 'custom',
          path: [...path, 'sectionCode'],
          message:
            `Section code ${station.sectionCode} is already used by ${holder}. ` +
            'A section code is unique across the whole establishment, so ' +
            `"${division.name} / ${station.name}" cannot also carry it.`,
        });
      }
    }
  });
